import React, { useMemo } from 'react';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
} from 'recharts';

interface HistoryPoint {
    date: string;
    total_value: number;
    cash: number;
    positions_value: number;
}

interface PortfolioHistoryChartProps {
    history: HistoryPoint[];
    totalInvested: number;
    isMobile?: boolean;
}

const formatFCFA = (v: number) => {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(0)}k`;
    return `${v}`;
};

const CustomTooltip = ({ active, payload, label, totalInvested }: any) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload as HistoryPoint;
    const perf = totalInvested > 0 ? ((d.total_value - totalInvested) / totalInvested) * 100 : 0;
    const isPositive = d.total_value >= totalInvested;

    return (
        <div style={{
            background: 'var(--bg-box)',
            border: '1px solid var(--border-color)',
            borderRadius: 10,
            padding: '12px 16px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            fontSize: 13,
            minWidth: 190,
        }}>
            <div style={{ fontWeight: 700, marginBottom: 8, color: 'var(--text-heading)' }}>
                {new Date(label).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 4 }}>
                <span style={{ color: 'var(--text-muted)' }}>Valeur totale</span>
                <span style={{ fontWeight: 700, color: '#a78bfa' }}>{d.total_value.toLocaleString()} FCFA</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 4 }}>
                <span style={{ color: 'var(--text-muted)' }}>Positions</span>
                <span style={{ fontWeight: 600 }}>{d.positions_value.toLocaleString()} FCFA</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 8 }}>
                <span style={{ color: 'var(--text-muted)' }}>Cash</span>
                <span style={{ fontWeight: 600 }}>{d.cash.toLocaleString()} FCFA</span>
            </div>
            <div style={{
                marginTop: 8,
                paddingTop: 8,
                borderTop: '1px solid var(--border-color)',
                display: 'flex',
                justifyContent: 'space-between',
                gap: 16,
            }}>
                <span style={{ color: 'var(--text-muted)' }}>Performance</span>
                <span style={{
                    fontWeight: 800,
                    color: isPositive ? '#10b981' : '#ef4444',
                    background: isPositive ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                    padding: '2px 8px',
                    borderRadius: 20,
                }}>
                    {isPositive ? '▲' : '▼'} {perf.toFixed(2)}%
                </span>
            </div>
        </div>
    );
};

const PortfolioHistoryChart: React.FC<PortfolioHistoryChartProps> = ({
    history,
    totalInvested,
    isMobile = false,
}) => {
    // Filter to weekdays with data (skip flat weekend stretches by deduplicating consecutive identical values)
    const chartData = useMemo(() => {
        if (!history || history.length === 0) return [];
        // Only keep dates where day of week is Mon-Fri to avoid flat weekend lines
        return history.filter(d => {
            const day = new Date(d.date).getDay();
            return day >= 1 && day <= 5;
        });
    }, [history]);

    if (chartData.length < 2) {
        return (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)', fontSize: 13 }}>
                Données insuffisantes pour afficher le graphe (minimum 2 jours de bourse).
            </div>
        );
    }

    const minVal = Math.min(...chartData.map(d => d.total_value));
    const maxVal = Math.max(...chartData.map(d => d.total_value));
    const padding = (maxVal - minVal) * 0.12;
    const yMin = Math.floor((minVal - padding) / 100_000) * 100_000;
    const yMax = Math.ceil((maxVal + padding) / 100_000) * 100_000;

    const currentVal = chartData[chartData.length - 1].total_value;
    const isPositive = currentVal >= totalInvested;
    const strokeColor = isPositive ? '#10b981' : '#ef4444';
    const fillId = isPositive ? 'green-gradient' : 'red-gradient';

    // X-axis ticks: one per week (every 5 trading days approx)
    const tickStep = Math.max(1, Math.floor(chartData.length / (isMobile ? 4 : 8)));
    const xTicks = chartData
        .filter((_, i) => i % tickStep === 0 || i === chartData.length - 1)
        .map(d => d.date);

    return (
        <div style={{ width: '100%' }}>
            <ResponsiveContainer width="100%" height={isMobile ? 200 : 260}>
                <AreaChart data={chartData} margin={{ top: 10, right: 10, bottom: 0, left: isMobile ? 0 : 10 }}>
                    <defs>
                        <linearGradient id="green-gradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.03} />
                        </linearGradient>
                        <linearGradient id="red-gradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.25} />
                            <stop offset="95%" stopColor="#ef4444" stopOpacity={0.03} />
                        </linearGradient>
                    </defs>

                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" opacity={0.5} />

                    <XAxis
                        dataKey="date"
                        ticks={xTicks}
                        tickFormatter={d => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                        tick={{ fontSize: isMobile ? 10 : 11, fill: 'var(--text-muted)' }}
                        axisLine={false}
                        tickLine={false}
                    />

                    <YAxis
                        domain={[yMin, yMax]}
                        tickFormatter={formatFCFA}
                        tick={{ fontSize: isMobile ? 10 : 11, fill: 'var(--text-muted)' }}
                        axisLine={false}
                        tickLine={false}
                        width={isMobile ? 38 : 52}
                    />

                    <Tooltip
                        content={<CustomTooltip totalInvested={totalInvested} />}
                        cursor={{ stroke: strokeColor, strokeWidth: 1, strokeDasharray: '4 2' }}
                    />

                    {/* Reference line at total invested capital */}
                    <ReferenceLine
                        y={totalInvested}
                        stroke="#64748b"
                        strokeDasharray="6 3"
                        label={isMobile ? undefined : {
                            value: `Capital ${(totalInvested / 1_000_000).toFixed(1)}M`,
                            position: 'insideTopRight',
                            fontSize: 11,
                            fill: '#64748b',
                        }}
                    />

                    <Area
                        type="monotone"
                        dataKey="total_value"
                        stroke={strokeColor}
                        strokeWidth={2}
                        fill={`url(#${fillId})`}
                        dot={false}
                        activeDot={{ r: 5, fill: strokeColor, stroke: 'white', strokeWidth: 2 }}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

export default PortfolioHistoryChart;
